import React, { useState } from 'react';

/* ─────────────────────────────────────────────────────────
 * DIFF TABLE & RECORDS TABLE
 * Tabular data sweeps with AI proposed changes & filter tags.
 * ───────────────────────────────────────────────────────── */

export function DiffTable({
  title = "Proposed Batch Production Adjustments (Next 7 Days)",
  headers = ["Flavor Item", "Previous Batch", "Proposed Batch", "Delta Status"],
  rows = [
    { type: "added", cells: ["Pistachio Royale", "120 L", "240 L", "+100% Increase"] },
    { type: "modified", cells: ["Mint Chocolate Chip", "300 L", "340 L", "+13.3% Adjusted"] },
    { type: "modified", cells: ["Classic Madagascar Vanilla", "400 L", "420 L", "+5.0% Steady"] },
    { type: "removed", cells: ["Rocky Road Campfire", "90 L", "0 L", "Discontinued"] },
  ]
}: {
  title?: string;
  headers?: string[];
  rows?: { type: 'added' | 'modified' | 'removed'; cells: string[] }[];
}) {
  return (
    <div className="w-full max-w-105 rounded-card bg-surface shadow-card border border-line overflow-hidden my-2">
      <div className="flex items-center justify-between px-3.5 py-2.5 bg-field border-b border-line">
        <span className="text-[12.5px] font-semibold text-ink">{title}</span>
        <span className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-[4px] bg-accent-tint text-accent font-semibold">
          AI Proposed Sweep
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-[12px]">
          <thead>
            <tr className="border-b border-line bg-field/50 text-[11px] text-ink-3 uppercase tracking-wider">
              {headers.map((h, i) => (
                <th key={i} className="px-3 py-2 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.map((row, i) => {
              const isAdd = row.type === 'added';
              const isRem = row.type === 'removed';

              return (
                <tr
                  key={i}
                  className={`transition-colors ${
                    isAdd ? 'bg-green-tint/40 text-green font-medium' : isRem ? 'bg-red-tint/30 text-red line-through opacity-75' : 'text-ink hover:bg-hover'
                  }`}
                >
                  {row.cells.map((cell, j) => (
                    <td key={j} className="px-3 py-2 tabular-nums">{cell}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function RecordsTable({
  title = "Global Creamery Records Directory",
  categories = ["All", "High Growth", "Steady", "Emerging"],
  records = [
    { name: "Andes Snow Creamery", city: "Quito, Ecuador", tag: "High Growth", score: "98/100", status: "Active" },
    { name: "Aurora Scoops", city: "Reykjavík, Iceland", tag: "Steady", score: "94/100", status: "Active" },
    { name: "Blue Fig Gelato", city: "Florence, Italy", tag: "High Growth", score: "99/100", status: "Partnered" },
    { name: "Cacao Norte", city: "Oaxaca, Mexico", tag: "Emerging", score: "89/100", status: "Active" },
    { name: "Kumo Creamery", city: "Tokyo, Japan", tag: "High Growth", score: "97/100", status: "Active" },
    { name: "Lumen Soft Serve", city: "Copenhagen, Denmark", tag: "Steady", score: "91/100", status: "Active" },
  ]
}: {
  title?: string;
  categories?: string[];
  records?: { name: string; city: string; tag: string; score: string; status: string }[];
}) {
  const [filter, setFilter] = useState("All");

  const filtered = filter === "All" ? records : records.filter(r => r.tag === filter);

  return (
    <div className="w-full max-w-105 rounded-card bg-surface shadow-card border border-line overflow-hidden my-2">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 px-3.5 py-2.5 border-b border-line bg-field">
        <span className="text-[12.5px] font-semibold text-ink">{title}</span>
        <div className="flex items-center gap-1">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setFilter(cat)}
              className={`px-2 py-0.5 rounded-[5px] text-[11px] font-medium transition-colors ${
                filter === cat ? 'bg-ink text-canvas font-semibold' : 'text-ink-3 hover:text-ink hover:bg-hover'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left text-[12px]">
          <thead>
            <tr className="border-b border-line bg-field/30 text-[11px] text-ink-3">
              <th className="px-3 py-1.5 font-medium">Company</th>
              <th className="px-3 py-1.5 font-medium">Location</th>
              <th className="px-3 py-1.5 font-medium">Tier</th>
              <th className="px-3 py-1.5 font-medium">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {filtered.map((r, i) => (
              <tr key={i} className="hover:bg-hover transition-colors">
                <td className="px-3 py-2 font-medium text-ink">{r.name}</td>
                <td className="px-3 py-2 text-ink-2">{r.city}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-1.5 py-0.5 rounded-[4px] font-mono text-[10px] ${
                    r.tag === 'High Growth' ? 'bg-green-tint text-green' : r.tag === 'Steady' ? 'bg-accent-tint text-accent' : 'bg-field text-ink-3'
                  }`}>
                    {r.tag}
                  </span>
                </td>
                <td className="px-3 py-2 text-ink-3 font-mono">{r.score}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
